import { useState } from "react";
import { Box, Typography } from "@mui/material";

import StyledButton from "../../NurtureProspects/ReusableComponents/Button";
import { mixpanelEvent, mixpanelCTAEvent } from "../../../Utils/mixpanelUtils";
import { brandImages } from "../../../theme/brandAssets";

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f4f4f4",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
  },
  rowWrap: {
    display: "flex",
    alignItems: "flex-start",
    gap: "24px",
  },
  logo: {
    height: "26px",
    width: "auto",
    flexShrink: 0,
  },
  card: {
    width: "100%",
    maxWidth: "980px",
    background: "#fff",
    borderRadius: "12px",
    padding: "32px 40px",
    boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.08)",
  },
  cardContent: {
    height: "700px",
    overflow: "hidden",
  },
  eyebrow: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    marginBottom: "8px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#000",
    marginBottom: "12px",
  },
  subtitle: {
    fontSize: "15px",
    color: "#374151",
    lineHeight: 1.5,
    marginBottom: "24px",
  },
  carousel: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginTop: "8px",
  },
  carouselArrow: {
    flexShrink: 0,
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    border: "1px solid #e5e5e5",
    background: "#fff",
    color: "#374151",
    fontSize: "15px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": { background: "#f4f4f4" },
  },
  carouselMediaWrap: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    justifyContent: "center",
  },
  carouselMedia: {
    width: "100%",
    maxWidth: "580px",
    height: "360px",
    borderRadius: "8px",
    overflow: "hidden",
    background: "#f4f4f4",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& img": { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" },
  },
  carouselBody: {
    maxWidth: "580px",
    margin: "14px auto 0",
    textAlign: "center",
  },
  subdots: {
    display: "flex",
    justifyContent: "center",
    gap: "6px",
    marginTop: "14px",
  },
  subdot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: "#e5e5e5",
    border: "none",
    padding: 0,
    cursor: "pointer",
  },
  subdotActive: {
    background: "#374151",
  },
  rowHeading: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#000",
    marginBottom: "6px",
  },
  rowText: {
    fontSize: "13px",
    color: "#6b7280",
    lineHeight: 1.5,
  },
  signalMedia: {
    width: "100%",
    maxWidth: "680px",
    height: "440px",
    borderRadius: "8px",
    overflow: "hidden",
    marginBottom: "16px",
    background: "#f4f4f4",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px auto",
    "& img": { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" },
  },
  nudge: {
    fontSize: "13px",
    color: "#6b7280",
    fontStyle: "italic",
    textAlign: "center",
  },
  footer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "28px",
    paddingTop: "20px",
    borderTop: "1px solid #e5e5e5",
  },
  dots: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    gap: "6px",
  },
  dot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    background: "#e5e5e5",
  },
  dotActive: {
    background: "#000",
  },
  footerActions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  skip: {
    background: "none",
    border: "none",
    color: "#9ca3af",
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    "&:hover": { color: "#6b7280" },
  },
};

const FEED_WAYS = [
  {
    key: "crmIntegrations",
    image: "/images/onboarding/CRM-integrations.png",
    heading: "Auto-sync directly from your recruitment CRM",
    text: "Connect JobAdder, Recruit CRM, ATZ CRM, or HubSpot to populate target accounts automatically with zero manual entry.",
  },
  {
    key: "saveProfileViaExt",
    image: "/images/onboarding/save-profile-via-ext.png",
    heading: "Save target profiles in one click as you browse",
    text: "Spot a key decision-maker on LinkedIn™? Click 'Save to Customer Feed' on their profile to track their activity immediately.",
  },
  {
    key: "customerFeedOverview",
    image: "/images/onboarding/Customer-Feed-2.png",
    heading: "100% key account visibility with zero feed noise",
    text: "Review posts, company updates, and leadership moves from priority accounts in one focused stream before daily outreach.",
  },
];

const COMMENT_WAYS = [
  {
    key: "agentMode",
    image: "/images/onboarding/Comment-AI-Agent-Mode.png",
    heading: "Autonomous AI Agent Mode",
    text: "Set your daily caps and active hours. Kynzo warms up your monitored client accounts with natural pacing while you focus on delivery.",
  },
  {
    key: "inFeedMode",
    image: "/images/onboarding/Comment-in-feed-Mode.png",
    heading: "In-Feed Native Commenting",
    text: "Browse your feed as usual. Kynzo injects contextual, practitioner-grade comments directly beside LinkedIn™'s comment box for instant posting.",
  },
  {
    key: "extensionMode",
    image: "/images/onboarding/Comment-Ext-Mode.png",
    heading: "Chrome Extension",
    text: "Open the side panel alongside any post to choose from 20 calibrated tones pre-trained on the A-A-O framework to craft sharp responses.",
  },
];

function Carousel({ items, activeIndex, onChange }) {
  const item = items[activeIndex];
  return (
    <>
      <Box sx={styles.carousel}>
        <Box
          component="button"
          type="button"
          sx={styles.carouselArrow}
          aria-label="Previous"
          onClick={() => onChange((activeIndex - 1 + items.length) % items.length)}
        >
          &larr;
        </Box>
        <Box sx={styles.carouselMediaWrap}>
          <Box sx={styles.carouselMedia}>
            <img src={item.image} alt={item.heading} />
          </Box>
        </Box>
        <Box
          component="button"
          type="button"
          sx={styles.carouselArrow}
          aria-label="Next"
          onClick={() => onChange((activeIndex + 1) % items.length)}
        >
          &rarr;
        </Box>
      </Box>
      <Box sx={styles.carouselBody}>
        <Typography sx={styles.rowHeading}>{item.heading}</Typography>
        <Typography sx={styles.rowText}>{item.text}</Typography>
      </Box>
      <Box sx={styles.subdots}>
        {items.map((it, i) => (
          <Box
            key={it.key}
            component="button"
            type="button"
            sx={{ ...styles.subdot, ...(i === activeIndex ? styles.subdotActive : {}) }}
            aria-label={`Item ${i + 1}`}
            onClick={() => onChange(i)}
          />
        ))}
      </Box>
    </>
  );
}

function CustomerFeedScreen({ subStep, setSubStep }) {
  return (
    <>
      <Typography sx={styles.eyebrow}>Stage 1: Customer Feed</Typography>
      <Typography sx={styles.title}>Stop scrolling the feed. Put target accounts in one place.</Typography>
      <Typography sx={styles.subtitle}>
        Scrolling the general LinkedIn™ feed hoping to catch a client post is a losing game.
        Your Customer Feed isolates live updates exclusively from the decision-makers who use your services,
        guaranteeing 100% key account visibility with zero wasted time.
      </Typography>
      <Carousel items={FEED_WAYS} activeIndex={subStep} onChange={setSubStep} />
    </>
  );
}

function CommentingScreen({ subStep, setSubStep }) {
  return (
    <>
      <Typography sx={styles.eyebrow}>Stage 2: Intelligent Engagement</Typography>
      <Typography sx={styles.title}>Show up before the job brief goes live with practitioner insight.</Typography>
      <Typography sx={styles.subtitle}>
        When hiring managers post, showing up first with thoughtful perspective builds top-of-mind authority.
        Choose from three native engagement modes to deliver 50 to 80 warm touchpoints daily without manual drafting.
      </Typography>
      <Carousel items={COMMENT_WAYS} activeIndex={subStep} onChange={setSubStep} />
    </>
  );
}

function SignalsScreen() {
  return (
    <>
      <Typography sx={styles.eyebrow}>Stage 3: Social Signals & Conversion</Typography>
      <Typography sx={styles.title}>Know the exact moment a hiring manager leans in.</Typography>
      <Typography sx={styles.subtitle}>
        Once an account is active in your feed, Kynzo monitors 5 high-intent buying signals (replies, comment likes,
        profile views, connection accepts, and inquiries) so you know precisely when to reach out and book the discovery call.
      </Typography>
      <Box sx={styles.signalMedia}>
        <img src="/images/onboarding/Social-Signal.png" alt="Kynzo Social Signals" />
      </Box>
      <Typography sx={styles.nudge}>"They engaged with your comment: now is the moment to start the conversation."</Typography>
    </>
  );
}

const SCREENS = [
  { key: "feed", buttonLabel: "Next" },
  { key: "comments", buttonLabel: "Next" },
  { key: "signals", buttonLabel: "Get started" },
];

function OnboardingWelcome() {
  const [step, setStep] = useState(0);
  const [commentSubStep, setCommentSubStep] = useState(0);
  const [feedSubStep, setFeedSubStep] = useState(0);

  const goToStep = (next) => {
    setCommentSubStep(0);
    setFeedSubStep(0);
    setStep(next);
    mixpanelEvent("onboarding_screen_viewed", { step: next, screen_name: "Onboarding Welcome" });
  };

  const finish = (buttonName) => {
    mixpanelCTAEvent({
      buttonName,
      ctaPosition: "hero",
      screenName: "onboarding welcome",
    });
    window.open("https://www.linkedin.com/", "_blank");
  };

  const isLastStep = step === SCREENS.length - 1;

  return (
    <Box sx={styles.page}>
      <Box sx={styles.rowWrap}>
        <img style={styles.logo} src={brandImages.wordmarkOnLight} alt="Kynzo" />
        <Box sx={styles.card}>
          <Box sx={styles.cardContent}>
            {step === 0 && <CustomerFeedScreen subStep={feedSubStep} setSubStep={setFeedSubStep} />}
            {step === 1 && <CommentingScreen subStep={commentSubStep} setSubStep={setCommentSubStep} />}
            {step === 2 && <SignalsScreen />}
          </Box>
          <Box sx={styles.footer}>
            <Box component="button" type="button" sx={styles.skip} onClick={() => finish("Skip")}>
              Skip
            </Box>
            <Box sx={styles.dots}>
              {SCREENS.map((s, i) => (
                <Box key={s.key} sx={{ ...styles.dot, ...(i === step ? styles.dotActive : {}) }} />
              ))}
            </Box>
            <Box sx={styles.footerActions}>
              <StyledButton
                variation="secondary"
                sx={{ visibility: step === 0 ? "hidden" : "visible" }}
                onClick={() => goToStep(step - 1)}
              >
                Back
              </StyledButton>
              <StyledButton
                onClick={() => (isLastStep ? finish("Get started") : goToStep(step + 1))}
              >
                {SCREENS[step].buttonLabel}
              </StyledButton>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default OnboardingWelcome;
